/**
 * D7: Djay Selection (mascot variant per account)
 * Source: src/mascot/useDjaySelection.ts
 */

import { USE_BACKEND } from '../helpers'

const DJAY_VARIANT_PREFIX = 'meester-djay-variant-'
const DJAY_ACCESSORY_PREFIX = 'meester-djay-accessory-'
const DJAY_TIER_PREFIX = 'meester-djay-tier-'
const DJAY_TREATMENT_PREFIX = 'meester-djay-treatment-'
const DJAY_LONGPRESS_PREFIX = 'meester-djay-longpress-'
const DJAY_EGG_PREFIX = 'meester-djay-egg-'
const DJAY_LAST_ERROR_TIP_PREFIX = 'djay_last_error_tip_'

export async function getDjayVariant(
  accountId: string | null,
): Promise<string | null> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  try {
    const key = accountId
      ? `${DJAY_VARIANT_PREFIX}${accountId}`
      : `${DJAY_VARIANT_PREFIX}global`
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

export async function saveDjayVariant(
  accountId: string | null,
  variant: string | null,
): Promise<void> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  try {
    const key = accountId
      ? `${DJAY_VARIANT_PREFIX}${accountId}`
      : `${DJAY_VARIANT_PREFIX}global`
    if (!variant || variant === 'default') {
      localStorage.removeItem(key)
    } else {
      localStorage.setItem(key, variant)
    }
  } catch {
    // silent
  }
}

export async function getDjayAccessory(
  accountId: string | null,
): Promise<string | null> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  try {
    const key = accountId
      ? `${DJAY_ACCESSORY_PREFIX}${accountId}`
      : `${DJAY_ACCESSORY_PREFIX}global`
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

export async function saveDjayAccessory(
  accountId: string | null,
  accessory: string | null,
): Promise<void> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  try {
    const key = accountId
      ? `${DJAY_ACCESSORY_PREFIX}${accountId}`
      : `${DJAY_ACCESSORY_PREFIX}global`
    if (!accessory || accessory === 'none') {
      localStorage.removeItem(key)
    } else {
      localStorage.setItem(key, accessory)
    }
  } catch {
    // silent
  }
}

export async function getDjayTier(
  accountId: string | null,
): Promise<string | null> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  try {
    const key = accountId
      ? `${DJAY_TIER_PREFIX}${accountId}`
      : `${DJAY_TIER_PREFIX}global`
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

export async function saveDjayTier(
  accountId: string | null,
  tier: string | null,
): Promise<void> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  try {
    const key = accountId
      ? `${DJAY_TIER_PREFIX}${accountId}`
      : `${DJAY_TIER_PREFIX}global`
    if (!tier || tier === 'rookie') {
      localStorage.removeItem(key)
    } else {
      localStorage.setItem(key, tier)
    }
  } catch {
    // silent
  }
}

export async function getDjayTreatment(
  accountId: string | null,
): Promise<string | null> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  try {
    const key = accountId
      ? `${DJAY_TREATMENT_PREFIX}${accountId}`
      : `${DJAY_TREATMENT_PREFIX}global`
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

export async function saveDjayTreatment(
  accountId: string | null,
  treatment: string | null,
): Promise<void> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  try {
    const key = accountId
      ? `${DJAY_TREATMENT_PREFIX}${accountId}`
      : `${DJAY_TREATMENT_PREFIX}global`
    if (!treatment || treatment === 'none') {
      localStorage.removeItem(key)
    } else {
      localStorage.setItem(key, treatment)
    }
  } catch {
    // silent
  }
}

/**
 * @deprecated Read-only: only used by the one-shot migration in
 * `useEasterEggs` that folds the legacy `longPressDiscovered` flag into
 * the `discoveredEggs` set. No writer exists — once every active learner
 * has been migrated this function (and `DJAY_LONGPRESS_PREFIX`) can be
 * removed entirely. Tracked in MASCOT_DOMAIN_AUDIT_2026 §DEAD-3.
 */
export async function getDjayLongPressDiscovered(
  accountId: string | null,
): Promise<boolean> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  try {
    const key = accountId
      ? `${DJAY_LONGPRESS_PREFIX}${accountId}`
      : `${DJAY_LONGPRESS_PREFIX}global`
    return localStorage.getItem(key) === 'true'
  } catch {
    return false
  }
}

export async function getDiscoveredEasterEggs(
  accountId: string | null,
): Promise<string[]> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  try {
    const key = accountId
      ? `${DJAY_EGG_PREFIX}${accountId}`
      : `${DJAY_EGG_PREFIX}global`
    const raw = localStorage.getItem(key)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed)
      ? parsed.filter((v) => typeof v === 'string')
      : []
  } catch {
    return []
  }
}

export async function saveDiscoveredEasterEggs(
  accountId: string | null,
  eggs: string[],
): Promise<void> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  try {
    const key = accountId
      ? `${DJAY_EGG_PREFIX}${accountId}`
      : `${DJAY_EGG_PREFIX}global`
    if (eggs.length === 0) {
      localStorage.removeItem(key)
    } else {
      localStorage.setItem(key, JSON.stringify(eggs))
    }
  } catch {
    // silent
  }
}

export async function getDjayLastErrorTipDate(
  accountId: string | null,
): Promise<string | null> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  try {
    if (!accountId) return null
    return localStorage.getItem(`${DJAY_LAST_ERROR_TIP_PREFIX}${accountId}`)
  } catch {
    return null
  }
}

export async function saveDjayLastErrorTipDate(
  accountId: string | null,
  isoDate: string,
): Promise<void> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  try {
    if (!accountId) return
    localStorage.setItem(`${DJAY_LAST_ERROR_TIP_PREFIX}${accountId}`, isoDate)
  } catch {
    // silent
  }
}
